import React from 'react'

import Icon from '../app_components/Icon'
import ToggleButton from '../form_sections/ToggleButton'
import LoaderSection from '../app_components/LoaderSection'
import { semantic } from '@lexody/design-tokens'

const NOTIFICATION_SECTIONS = [
  {
    title: 'New Message',
    icon: 'comment-dots',
    preview:
      'JohnDoe says: Yes, Spain is my favorite place to travel. That is why I want to practice my Spanish.',
    pushKey: 'msg_push',
    emailKey: 'msg_email',
    requiresOne: true,
  },
  {
    title: 'New Match',
    icon: 'users-alt',
    preview:
      'JohnDoe is learning Spanish, and speaks English in Boston. Want to connect?',
    pushKey: 'match_push',
    emailKey: 'match_email',
  },
  {
    title: 'Manage Connects',
    icon: 'users-alt',
    preview:
      'JohnDoe wants to connect! Click here to accept and schedule a Lex!',
    pushKey: 'connect_push',
    emailKey: 'connect_email',
  },
  {
    title: 'Scheduling a Lex',
    icon: 'calendar',
    preview:
      'JohnDoe wants to Lex on Monday at 7pm. Respond to the request and plan your activities!',
    pushKey: 'lex_push',
    emailKey: 'lex_email',
  },
  {
    title: 'Weekly Updates',
    icon: 'bell',
    preview:
      'Weekly email with language learning resources, product updates, and all things languages!',
    pushKey: 'updates_push',
    emailKey: 'updates_email',
  },
  {
    title: 'Getting Started',
    icon: 'rocket',
    preview:
      'Tips and suggestions to get the most out of Lexody, such as tips for your profile and planning your first Lex.',
    pushKey: 'onboard_push',
    emailKey: 'onboard_email',
  },
]

function NotificationSectionCard({ title, icon, preview, children }) {
  return (
    <section className="lex-section-card notification-settings__card">
      <header className="lex-section-card__header">
        <div className="lex-section-card__titleRow">
          <span className="lex-section-card__iconCircle" aria-hidden="true">
            <Icon name={icon} size={20} color={semantic.premium.text.label} />
          </span>
          <h2 className="lex-section-card__title">{title}</h2>
        </div>
      </header>
      <p className="notification-settings__preview">{preview}</p>
      <div className="lex-form-section notification-settings__toggles">
        {children}
      </div>
    </section>
  )
}

class NotificationSettings extends React.Component {
  constructor(props) {
    super(props)

    const initial = props.notificationsUser || {}

    this.state = {
      id: initial.id,
      msg_email: initial.msg_email !== false,
      msg_push: initial.msg_push !== false,
      match_email: initial.match_email !== false,
      match_push: initial.match_push !== false,
      connect_email: initial.connect_email !== false,
      connect_push: initial.connect_push !== false,
      lex_email: initial.lex_email !== false,
      lex_push: initial.lex_push !== false,
      updates_email: initial.updates_email !== false,
      updates_push: initial.updates_push !== false,
      onboard_email: initial.onboard_email !== false,
      onboard_push: initial.onboard_push !== false,
      msgError: false,
      saved: false,
      saveTimeout: null,
      loaded: Boolean(initial.id),
    }

    this.updateValue = this.updateValue.bind(this)
    this.updateMessageValue = this.updateMessageValue.bind(this)
    this.saveSettings = this.saveSettings.bind(this)
    this.savedAlert = this.savedAlert.bind(this)
    this.savedTimer = this.savedTimer.bind(this)
  }

  componentWillUnmount() {
    clearTimeout(this.state.saveTimeout)
  }

  savedTimer() {
    clearTimeout(this.state.saveTimeout)
    this.setState({
      saveTimeout: setTimeout(() => {
        this.setState({ saved: false })
      }, 1500),
    })
  }

  saveSettings(overrides = {}) {
    const data = {
      notifications_user: {
        msg_email: this.state.msg_email,
        msg_push: this.state.msg_push,
        match_email: this.state.match_email,
        match_push: this.state.match_push,
        connect_email: this.state.connect_email,
        connect_push: this.state.connect_push,
        lex_email: this.state.lex_email,
        lex_push: this.state.lex_push,
        updates_email: this.state.updates_email,
        updates_push: this.state.updates_push,
        onboard_email: this.state.onboard_email,
        onboard_push: this.state.onboard_push,
        ...overrides,
      },
    }

    $.ajax({
      url: `../notifications_users/${this.state.id}`,
      dataType: 'JSON',
      type: 'PATCH',
      data,
      success: () => {
        this.setState({ saved: true }, () => this.savedTimer())
      },
      error: (xhr, status, err) => {
        console.log(err)
      },
    })
  }

  updateValue(prop) {
    const nextValue = !this.state[prop]

    this.setState({ [prop]: nextValue, msgError: false }, () => {
      this.saveSettings({ [prop]: nextValue })
    })
  }

  updateMessageValue(prop) {
    const nextValue = !this.state[prop]

    if (this.props.hasPushToken && nextValue === false) {
      if (prop === 'msg_email' && !this.state.msg_push) {
        this.setState({ msgError: true })
        return
      }
      if (prop === 'msg_push' && !this.state.msg_email) {
        this.setState({ msgError: true })
        return
      }
    }

    this.setState({ [prop]: nextValue, msgError: false }, () => {
      this.saveSettings({ [prop]: nextValue })
    })
  }

  savedAlert() {
    return (
      <div className="lex-form-toast" role="status">
        <Icon
          name="check-circle"
          size={20}
          color={semantic.success.text.label}
          solid
        />
        <h3>Preferences saved</h3>
      </div>
    )
  }

  render() {
    const { hasPushToken } = this.props

    if (!this.state.loaded) {
      return <LoaderSection />
    }

    return (
      <div className="lex-form-page lex-form-page--narrow notification-settings">
        {this.state.saved && this.savedAlert()}

        <header className="lex-form-page__header">
          <h1 className="lex-form-page__title">Notifications</h1>
          <p className="lex-form-page__subtitle">
            {hasPushToken
              ? 'Choose how you want to hear about messages, matches, and Lexody updates. Changes save automatically.'
              : 'Manage your email notifications. Push settings appear here after you sign in on the Lexody mobile app.'}
          </p>
        </header>

        {NOTIFICATION_SECTIONS.map((section) => {
          const onToggle = section.requiresOne && hasPushToken
            ? this.updateMessageValue
            : this.updateValue

          return (
            <NotificationSectionCard
              key={section.title}
              title={section.title}
              icon={section.icon}
              preview={section.preview}
            >
              {hasPushToken && (
                <ToggleButton
                  buttonText="Push notification"
                  selected={this.state[section.pushKey]}
                  onClick={() => onToggle(section.pushKey)}
                />
              )}
              <ToggleButton
                buttonText="Email notification"
                selected={this.state[section.emailKey]}
                onClick={() => onToggle(section.emailKey)}
              />
              {section.requiresOne && hasPushToken && this.state.msgError && (
                <p className="lex-field__error notification-settings__error">
                  At least one notification method is required for new messages.
                </p>
              )}
            </NotificationSectionCard>
          )
        })}
      </div>
    )
  }
}

export default NotificationSettings
